import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const CASES_LIST_PAGE_PARAM = 'page';
export const DIRECTORY_PAGE_PARAM = 'dir_page';
export const ACTIVE_CASES_PAGE_SIZE = 100;
export const DIRECTORY_PAGE_SIZE = 100;
export const ADVISOR_CASES_LIST_PATH = '/es/advisor';
export const ADVISOR_CASES_LIST_RETURN_KEY = 'advisorCasesListReturnTo';

export function parseCasesListPage(searchParams) {
  const raw = searchParams?.get?.(CASES_LIST_PAGE_PARAM);
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

export function casesListPageToSearchParams(prev, page) {
  const sp = new URLSearchParams(prev);
  const p = Math.max(1, Math.floor(Number(page)) || 1);
  if (p <= 1) sp.delete(CASES_LIST_PAGE_PARAM);
  else sp.set(CASES_LIST_PAGE_PARAM, String(p));
  return sp;
}

export function parseDirectoryListPage(searchParams) {
  const raw = searchParams?.get?.(DIRECTORY_PAGE_PARAM);
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

export function directoryPageToSearchParams(prev, page) {
  const sp = new URLSearchParams(prev);
  const p = Math.max(1, Math.floor(Number(page)) || 1);
  if (p <= 1) sp.delete(DIRECTORY_PAGE_PARAM);
  else sp.set(DIRECTORY_PAGE_PARAM, String(p));
  return sp;
}

export function advisorCasesListPath(search = '') {
  if (!search) return ADVISOR_CASES_LIST_PATH;
  const q = search.startsWith('?') ? search.slice(1) : search;
  return q ? `${ADVISOR_CASES_LIST_PATH}?${q}` : ADVISOR_CASES_LIST_PATH;
}

export function persistAdvisorCasesListReturn(path) {
  try {
    if (path) sessionStorage.setItem(ADVISOR_CASES_LIST_RETURN_KEY, path);
  } catch {
    /* ignore quota / private mode */
  }
}

export function readAdvisorCasesListReturn() {
  try {
    return sessionStorage.getItem(ADVISOR_CASES_LIST_RETURN_KEY) || ADVISOR_CASES_LIST_PATH;
  } catch {
    return ADVISOR_CASES_LIST_PATH;
  }
}

/** Sync active-cases list page with `?page=` so browser back restores pagination. */
export function useCasesListPageFromUrl() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => parseCasesListPage(searchParams), [searchParams]);

  const setPage = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const current = parseCasesListPage(prev);
          const resolved = typeof next === 'function' ? next(current) : next;
          return casesListPageToSearchParams(prev, resolved);
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const returnTo = useMemo(() => {
    const q = searchParams.toString();
    return advisorCasesListPath(q ? `?${q}` : '');
  }, [searchParams]);

  return { page, setPage, returnTo, searchParams };
}

/** Sync customer-directory list page with `?dir_page=` (Customer Search tab). */
export function useDirectoryListPageFromUrl() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => parseDirectoryListPage(searchParams), [searchParams]);

  const setPage = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const current = parseDirectoryListPage(prev);
          const resolved = typeof next === 'function' ? next(current) : next;
          return directoryPageToSearchParams(prev, resolved);
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { page, setPage, searchParams };
}
